// Pure helpers for choosing a recordable audio/video MIME type and deriving the
// matching file extension.
//
// Why this exists: iOS Safari's MediaRecorder ONLY supports the MP4/AAC
// container — it cannot record WebM, and constructing
// `new MediaRecorder(stream, { mimeType: 'audio/webm' })` THROWS on iOS. The
// app previously hard-coded `audio/webm` for speaking + AI-interview recordings
// and a `.webm` filename on upload, so iPhone recordings failed outright and
// (where they didn't) reached the STT backend mislabelled. These helpers pick
// the first container the current browser can actually record and keep the
// container label consistent end-to-end (blob type -> filename extension ->
// upload Content-Type -> Deepgram/ffmpeg).
//
// Everything here is pure and dependency-injectable (the `isSupported` probe is
// passed in) so it is unit-testable without a browser/MediaRecorder.

// Ordered by preference. Opus-in-WebM first (smallest, ideal for desktop Chrome
// and Deepgram), then MP4/AAC which is what iOS Safari produces. First match wins.
export const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2', // AAC-LC — iOS Safari
  'audio/mp4',
  'audio/aac',
];

export const VIDEO_MIME_CANDIDATES = [
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1', // H.264 — iOS Safari
  'video/mp4',
];

// Resolve the MediaRecorder.isTypeSupported probe, or null if MediaRecorder is
// unavailable (SSR / very old browser). Exposed mainly so callers/tests can
// override it.
export const defaultIsTypeSupported = () => {
  if (
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function'
  ) {
    return (t) => {
      try {
        return MediaRecorder.isTypeSupported(t);
      } catch (_) {
        return false;
      }
    };
  }
  return null;
};

// Pick the first supported MIME type from `candidates`. Returns '' when none
// match (or no probe is available) — callers should then construct the
// MediaRecorder WITHOUT an explicit mimeType and let the browser default.
export const pickSupportedMimeType = (candidates, isSupported) => {
  const probe = isSupported || defaultIsTypeSupported();
  if (typeof probe !== 'function') return '';
  for (const type of candidates || []) {
    if (probe(type)) return type;
  }
  return '';
};

export const pickAudioMimeType = (isSupported) =>
  pickSupportedMimeType(AUDIO_MIME_CANDIDATES, isSupported);

export const pickVideoMimeType = (isSupported) =>
  pickSupportedMimeType(VIDEO_MIME_CANDIDATES, isSupported);

// Map a MIME type (which may carry `;codecs=...`) to a bare file extension.
// We deliberately use `mp4` (not `m4a`) for audio in an MP4 container: Deepgram
// processes `audio/mp4` reliably, whereas some `.m4a` containers hit a known
// Deepgram demux quirk.
export const extensionForMimeType = (mimeType, fallbackExt = 'webm') => {
  const m = String(mimeType || '').toLowerCase();
  if (!m) return fallbackExt;
  if (m.includes('webm')) return 'webm';
  if (m.includes('ogg') || m.includes('opus')) return 'ogg';
  if (m.includes('mp4') || m.includes('m4a') || m.includes('aac') || m.includes('mpeg4'))
    return 'mp4';
  if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
  if (m.includes('wav') || m.includes('wave')) return 'wav';
  return fallbackExt;
};

// "<base>.<ext>" using the extension implied by `mimeType`.
export const filenameForMimeType = (base, mimeType, fallbackExt = 'webm') =>
  `${base}.${extensionForMimeType(mimeType, fallbackExt)}`;

// Resolve the effective container type of a finished recording. Prefer the
// recorder's negotiated `mimeType`, then the captured selection, then the
// blob's own type, then a safe fallback. `mimeType` here may include codecs;
// strip them for a clean Content-Type when needed via `baseMimeType`.
export const resolveRecordedMimeType = (
  recorder,
  selectedMimeType,
  blob,
  fallback = 'audio/webm'
) =>
  (recorder && recorder.mimeType) ||
  selectedMimeType ||
  (blob && blob.type) ||
  fallback;

// Strip any `;codecs=...` parameter, e.g. 'audio/mp4;codecs=mp4a.40.2' -> 'audio/mp4'.
export const baseMimeType = (mimeType, fallback = 'audio/webm') => {
  const m = String(mimeType || '').split(';')[0].trim();
  return m || fallback;
};
